import React, { useState } from "react";
import { Eye, EyeOff, Pencil, Trash2, Plus, X } from "lucide-react";
import StatusBadge from "./StatusBadge";
import EmptyState from "./EmptyState";
import ConfirmModal from "./ConfirmModal";
import relativeTime from "../../util/relativeTime";

const ROLE_OPTIONS = ["tenant_admin", "support_agent", "viewer"];

const emptyRow = () => ({ name: "", email: "", role: "viewer", password: "", showPassword: false });

const passFeedback = (pass) => [
  { label: "At least 8 characters", ok: pass.length >= 8 },
  { label: "One special character", ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass) },
  { label: "One number", ok: /\d/.test(pass) },
];

const TenantTeamTab = ({ users, disabled, onBulkInvite, onBulkDeleteUsers, onUpdateUserRole, onDeleteUser }) => {
  const [showForm, setShowForm] = useState(false);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(emptyRow());
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState(emptyRow());
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);
  const [roleConfirm, setRoleConfirm] = useState({ open: false, userId: null, newRole: null, userName: "", oldRole: "" });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null, userName: "" });
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [lastAdminGuard, setLastAdminGuard] = useState({ open: false, userName: "" });
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const adminCount = (users || []).filter((u) => u.role === "tenant_admin").length;

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return "Enter a valid email address.";
    const duplicate = members.some((m, i) => i !== editingIndex && m.email.trim().toLowerCase() === email.trim().toLowerCase());
    if (duplicate) return "This email is already in the list.";
    return "";
  };

  const handleAdd = () => {
    if (!form.name.trim()) {
      setFormError("Name is required.");
      return;
    }
    const emailErr = validateEmail(form.email);
    if (emailErr) {
      setFormError(emailErr);
      return;
    }
    const passwordChecks = passFeedback(form.password);
    if (!passwordChecks.every((c) => c.ok)) {
      setFormError("Password must be at least 8 characters, include one special character, and one number.");
      return;
    }
    setFormError("");
    setMembers((prev) => [...prev, { ...form, name: form.name.trim(), email: form.email.trim().toLowerCase(), showPassword: false }]);
    setForm(emptyRow());
  };

  const handleEditStart = (index) => {
    setEditingIndex(index);
    setEditForm({ ...members[index] });
    setFormError("");
  };

  const handleEditSave = () => {
    if (!editForm.name.trim()) {
      setFormError("Name is required.");
      return;
    }
    const emailErr = validateEmail(editForm.email);
    if (emailErr) {
      setFormError(emailErr);
      return;
    }
    const passwordChecks = passFeedback(editForm.password);
    if (!passwordChecks.every((c) => c.ok)) {
      setFormError("Password must be at least 8 characters, include one special character, and one number.");
      return;
    }
    setFormError("");
    setMembers((prev) => prev.map((m, i) => i === editingIndex ? { ...editForm, name: editForm.name.trim(), email: editForm.email.trim().toLowerCase(), showPassword: false } : m));
    setEditingIndex(null);
    setEditForm(emptyRow());
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditForm(emptyRow());
    setFormError("");
  };

  const handleRemoveMember = (index) => {
    setMembers((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditForm(emptyRow());
    }
  };

  const handleSend = async () => {
    setSending(true);
    setFormError("");
    try {
      await onBulkInvite(members);
      setMembers([]);
      setShowForm(false);
    } catch (err) {
      setFormError(err.message || "Failed to add members.");
    } finally {
      setSending(false);
    }
  };

  const handleRoleChange = (user, newRole) => {
    if (newRole === user.role) return;
    const isLastAdmin = user.role === "tenant_admin" && adminCount <= 1 && newRole !== "tenant_admin";
    if (isLastAdmin) {
      setLastAdminGuard({ open: true, userName: user.name || user.email || "this member" });
      return;
    }
    setRoleConfirm({
      open: true,
      userId: user.id,
      newRole,
      oldRole: user.role,
      userName: user.name || user.email || "this member",
    });
  };

  const handleRoleConfirm = async () => {
    const { userId, newRole } = roleConfirm;
    setRoleConfirm({ open: false, userId: null, newRole: null, userName: "", oldRole: "" });
    setActionLoading(userId);
    setActionError("");
    try {
      await onUpdateUserRole(userId, { role: newRole });
    } catch (err) {
      setActionError(err.message || "Failed to update role.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (user) => {
    const isLastAdmin = user.role === "tenant_admin" && adminCount <= 1;
    if (isLastAdmin) {
      setLastAdminGuard({ open: true, userName: user.name || user.email || "this member" });
      return;
    }
    setDeleteConfirm({ open: true, userId: user.id, userName: user.name || user.email || "this member" });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === (users || []).length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set((users || []).map((u) => u.id)));
    }
  };

  const handleBulkDeleteConfirm = async () => {
    const ids = Array.from(selectedIds);
    setBulkDeleteConfirm(false);
    setSelectedIds(new Set());
    setActionLoading("bulk");
    setActionError("");
    try {
      await onBulkDeleteUsers(ids);
    } catch (err) {
      setActionError(err.message || "Failed to remove members.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteConfirm = async () => {
    const { userId } = deleteConfirm;
    setDeleteConfirm({ open: false, userId: null, userName: "" });
    setActionLoading(userId);
    setActionError("");
    try {
      await onDeleteUser(userId);
    } catch (err) {
      setActionError(err.message || "Failed to remove member.");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-950">
            {users ? users.length : 0} member{users && users.length !== 1 ? "s" : ""}
            {members.length > 0 ? ` + ${members.length} pending` : ""}
          </p>
          {!disabled && !showForm ? (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#0959bb]"
            >
              <Plus size={16} />
              Add member
            </button>
          ) : null}
        </div>

        {actionError ? (
          <div className="mx-5 mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-700">
            {actionError}
          </div>
        ) : null}

        {showForm ? (
          <div className="border-b border-slate-100">
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Add new members</p>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setMembers([]); setForm(emptyRow()); setFormError(""); }}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-[1fr_1fr_140px]">
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Full name"
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/30"
                    />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="email@company.com"
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/30"
                    />
                    <select
                      value={form.role}
                      onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/30"
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>{r.replace("_", " ")}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                    <div className="relative">
                      <input
                        type={form.showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder="Password"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/30"
                      />
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, showPassword: !prev.showPassword }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {form.showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleAdd}
                      className="rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb]"
                    >
                      Add
                    </button>
                  </div>
                  {form.password && (
                    <div className="flex gap-4">
                      {passFeedback(form.password).map((item) => (
                        <p key={item.label} className={`text-xs ${item.ok ? "text-green-600" : "text-slate-400"}`}>
                          {item.ok ? "\u2713" : "\u2022"} {item.label}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                {formError ? (
                  <p className="mt-2 text-xs text-rose-600">{formError}</p>
                ) : null}
              </div>

              {members.length > 0 ? (
                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50 text-left">
                        <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Name</th>
                        <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Email</th>
                        <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Password</th>
                        <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Role</th>
                        <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {members.map((member, index) => {
                        const isEditing = editingIndex === index;
                        return (
                          <tr key={index} className="hover:bg-slate-50 transition-colors">
                            {isEditing ? (
                              <>
                                <td className="px-4 py-2.5">
                                  <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/30"
                                  />
                                </td>
                                <td className="px-4 py-2.5">
                                  <input
                                    type="email"
                                    value={editForm.email}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/30"
                                  />
                                </td>
                                <td className="px-4 py-2.5">
                                  <div className="relative">
                                    <input
                                      type={editForm.showPassword ? "text" : "password"}
                                      value={editForm.password}
                                      onChange={(e) => setEditForm((prev) => ({ ...prev, password: e.target.value }))}
                                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 pr-8 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/30"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setEditForm((prev) => ({ ...prev, showPassword: !prev.showPassword }))}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                      {editForm.showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                  </div>
                                </td>
                                <td className="px-4 py-2.5">
                                  <select
                                    value={editForm.role}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, role: e.target.value }))}
                                    className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/30"
                                  >
                                    {ROLE_OPTIONS.map((r) => (
                                      <option key={r} value={r}>{r.replace("_", " ")}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                  <div className="flex items-center justify-end gap-1.5">
                                    <button type="button" onClick={handleEditSave} className="rounded-lg bg-[#096dea] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#0959bb]">Save</button>
                                    <button type="button" onClick={handleEditCancel} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-2.5 font-medium text-slate-900">{member.name}</td>
                                <td className="px-4 py-2.5 text-slate-500">{member.email}</td>
                                <td className="px-4 py-2.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-sm text-slate-500">
                                      {member.showPassword ? member.password : "\u2022".repeat(Math.min(member.password.length, 12))}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setMembers((prev) => prev.map((m, i) => i === index ? { ...m, showPassword: !m.showPassword } : m))}
                                      className="text-slate-400 hover:text-slate-600"
                                    >
                                      {member.showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                  </div>
                                </td>
                                <td className="px-4 py-2.5"><StatusBadge status={member.role} /></td>
                                <td className="px-4 py-2.5 text-right">
                                  <div className="flex items-center justify-end gap-1.5">
                                    <button type="button" onClick={() => handleEditStart(index)} className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700" title="Edit">
                                      <Pencil size={14} />
                                    </button>
                                    <button type="button" onClick={() => handleRemoveMember(index)} className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-600 hover:bg-rose-100" title="Remove">
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-sm text-slate-400 py-6">No members added yet. Use the form above to add team members.</p>
              )}

              {members.length > 0 ? (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={sending}
                    className="rounded-xl bg-[#096dea] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0959bb] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? "Adding..." : `Add ${members.length} member${members.length !== 1 ? "s" : ""}`}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Existing users table */}
        {users && users.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-left">
                {!disabled ? (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === users.length && users.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-slate-300 text-[#096dea] focus:ring-[#096dea]"
                    />
                  </th>
                ) : null}
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Member</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Email</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Role</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Joined</th>
                {!disabled ? <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">Remove</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user) => {
                const isSaving = actionLoading === user.id;
                const isSelected = selectedIds.has(user.id);

                return (
                  <tr key={user.id} className={`transition-colors ${isSelected ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                    {!disabled ? (
                      <td className="px-4 py-4 w-10">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(user.id)}
                          className="h-4 w-4 rounded border-slate-300 text-[#096dea] focus:ring-[#096dea]"
                        />
                      </td>
                    ) : null}
                    <td className="px-5 py-4 font-medium text-slate-900">
                      {user.name || user.invited_email || user.email || "Member"}
                    </td>
                    <td className="px-5 py-4 text-slate-500">
                      {user.email || user.invited_email || "Invite pending"}
                    </td>
                    <td className="px-5 py-4">
                      <StatusBadge status={user.status} />
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user, e.target.value)}
                        disabled={disabled || isSaving}
                        aria-label={`Role for ${user.name || user.email || "member"}`}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-900 outline-none focus:ring-2 focus:ring-[#096dea] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>{r.replace("_", " ")}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-500">
                      {user.status === "invited"
                        ? <span className="text-slate-400 italic">Invited</span>
                        : relativeTime(user.joined_at || user.created_at)
                      }
                    </td>
                    {!disabled ? (
                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(user)}
                          disabled={isSaving}
                          className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-600 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : !showForm ? (
          <EmptyState
            title="No tenant users yet"
            description="Invite team members to this tenant workspace. Roles available: tenant_admin, support_agent, viewer."
          />
        ) : null}
      </div>

      {selectedIds.size > 0 ? (
        <div className="flex items-center justify-between rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3">
          <p className="text-sm text-rose-800">
            <span className="font-semibold">{selectedIds.size}</span> member{selectedIds.size !== 1 ? "s" : ""} selected
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                const selectedUsers = (users || []).filter((u) => selectedIds.has(u.id));
                const selectedAdmins = selectedUsers.filter((u) => u.role === "tenant_admin");
                if (selectedAdmins.length > 0 && adminCount - selectedAdmins.length <= 0) {
                  setLastAdminGuard({ open: true, userName: selectedAdmins.length === 1 ? selectedAdmins[0].name || selectedAdmins[0].email || "this member" : `${selectedAdmins.length} admins` });
                  return;
                }
                setBulkDeleteConfirm(true);
              }}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
            >
              Remove {selectedIds.size} member{selectedIds.size !== 1 ? "s" : ""}
            </button>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={roleConfirm.open}
        title="Change member role"
        description={`Change ${roleConfirm.userName}'s role from ${roleConfirm.oldRole?.replace("_", " ")} to ${roleConfirm.newRole?.replace("_", " ")}?`}
        confirmLabel="Confirm change"
        cancelLabel="Cancel"
        onConfirm={handleRoleConfirm}
        onCancel={() => setRoleConfirm({ open: false, userId: null, newRole: null, userName: "", oldRole: "" })}
      />

      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove member"
        description={`Remove ${deleteConfirm.userName} from this tenant? They will lose access to this workspace.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ open: false, userId: null, userName: "" })}
      />

      <ConfirmModal
        open={bulkDeleteConfirm}
        title={`Remove ${selectedIds.size} member${selectedIds.size !== 1 ? "s" : ""}`}
        description={`Remove ${selectedIds.size} selected member${selectedIds.size !== 1 ? "s" : ""} from this tenant? They will lose access to this workspace.`}
        confirmLabel={actionLoading === "bulk" ? "Removing..." : "Remove"}
        cancelLabel="Cancel"
        onConfirm={handleBulkDeleteConfirm}
        onCancel={() => setBulkDeleteConfirm(false)}
        loading={actionLoading === "bulk"}
      />

      <ConfirmModal
        open={lastAdminGuard.open}
        title="Cannot remove last admin"
        description={`${lastAdminGuard.userName} ${lastAdminGuard.userName.endsWith("dmins") ? "are" : "is"} the only tenant admin${lastAdminGuard.userName.endsWith("dmins") ? "s" : ""} for this workspace. Another admin must be assigned before ${lastAdminGuard.userName.endsWith("dmins") ? "they can be" : "this admin can be"} removed or downgraded.`}
        confirmLabel="Keep admin"
        cancelLabel="Close"
        onConfirm={() => setLastAdminGuard({ open: false, userName: "" })}
        onCancel={() => setLastAdminGuard({ open: false, userName: "" })}
      />
    </>
  );
};

export default TenantTeamTab;
